import { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { notify } from '../utils/notify';
import { invalidateCatalog } from '../lib/catalog';

interface UseInlineEditReturn {
  isEditing: boolean;
  isSaving: boolean;
  setIsEditing: (editing: boolean) => void;
  handleSave: (newValue: any) => Promise<void>;
}

/**
 * Custom hook for inline editing with optimistic updates
 * @param collection - Firestore collection name ('videos', 'agencies', 'companies')
 * @param itemId - Document ID
 * @param field - Field name to update
 * @param onUpdate - Callback to update local state optimistically
 * @param onError - Callback to rollback on error
 */
export function useInlineEdit(
  collection: string,
  itemId: string,
  field: string,
  onUpdate: (newValue: any) => void,
  onError: () => void
): UseInlineEditReturn {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async (newValue: any) => {
    setIsSaving(true);

    // Optimistic update
    onUpdate(newValue);

    try {
      const docRef = doc(db, collection, itemId);
      await updateDoc(docRef, { [field]: newValue });
      invalidateCatalog();

      notify({
        isSuccess: true,
        message: 'Modification enregistrée',
      });
    } catch (error) {
      // Rollback on error
      onError();

      notify({
        isSuccess: false,
        message: 'Modification impossible',
        info: error instanceof Error ? error.message : 'Réessayez dans un instant.',
      });

      console.error('Inline edit error:', error);
    } finally {
      setIsSaving(false);
      setIsEditing(false);
    }
  };

  return {
    isEditing,
    isSaving,
    setIsEditing,
    handleSave,
  };
}
